import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { cx } from '@emotion/css'

import AccessibleIcon from '../AccessibleIcon'
import { colors } from '../../themes'
import { specialKeyPressed } from '../../helpers/eventHelpers'

import styles, { hoverTheme, iconTheme } from './styles'

const ActionsMenu = ({ icon, className, children, theme = 'primary', label = 'further actions', onToggle = () => {} }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  const [isFocussed, setIsFocussed] = useState(false)
  const node = useRef()

  const displayIconColor = isHovering || isOpen || isFocussed

  const handleButtonClick = () => {
    onToggle(!isOpen)
    setIsOpen(!isOpen)
  }

  const handleMenuItemClick = () => {
    setIsOpen(false)
    setIsHovering(false)
  }

  useEffect(() => {
    const handleClosingClick = e => {
      if (node.current.contains(e.target)) return
      onToggle(false)
      setIsOpen(false)
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClosingClick)
    } else {
      document.removeEventListener('mousedown', handleClosingClick)
    }

    return () => {
      document.removeEventListener('mousedown', handleClosingClick)
    }
  }, [isOpen, onToggle])

  return (
    <div
      className={cx(styles.actionsMenu, className)}
      ref={node}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}>
      <button
        type="button"
        onClick={handleButtonClick}
        aria-haspopup="true"
        aria-expanded={isOpen}
        className={cx(styles.button, hoverTheme(theme))}
        aria-label={label}
        onFocus={() => setIsFocussed(true)}
        onBlur={() => setIsFocussed(false)}>
        <AccessibleIcon icon={icon} size={18} color={displayIconColor ? iconTheme(theme) : colors.jumbo} />
      </button>
      {Boolean(isOpen) && (
        <div
          role="menu"
          tabIndex={0}
          className={styles.menu}
          aria-label={`${label} menu`}
          onKeyDown={ev => {
            if (ev.code === 13 && !specialKeyPressed(ev)) setIsOpen(false)
          }}
          onClick={handleMenuItemClick}>
          {React.Children.map(children, child => {
            if (!child) return null
            const item = React.cloneElement(child, {
              theme,
            })
            return item
          })}
        </div>
      )}
    </div>
  )
}

ActionsMenu.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  theme: PropTypes.oneOf(['', 'primary', 'secondary', 'success', 'warning', 'error', 'legacy', 'light', 'dark', 'tertiary', 'info']),
  icon: PropTypes.oneOfType([PropTypes.element, PropTypes.shape({})]).isRequired,
  className: PropTypes.string,
  label: PropTypes.string,
  onToggle: PropTypes.func,
}

export default ActionsMenu
